import { useState, useCallback } from 'react';
import { analyticsApi } from '../api/analytics.api';
import type { TimePeriod } from '../types';

interface UseExportResult {
    isExporting: boolean;
    exportData: (format: 'csv' | 'png', filename?: string) => Promise<void>;
    error: string | null;
}

export const useExport = (
    period: TimePeriod,
    siteIds: string[]
): UseExportResult => {
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const exportData = useCallback(async (
        format: 'csv' | 'png',
        filename?: string
    ) => {
        setIsExporting(true);
        setError(null);

        try {
            const blob = await analyticsApi.exportData(period, format, siteIds);
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || `analytics-${period}-${Date.now()}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Export failed');
            throw err;
        } finally {
            setIsExporting(false);
        }
    }, [period, siteIds]);

    return { isExporting, exportData, error };
};